import React, { memo } from 'react';
import { Card, Row, Col, Progress, Tooltip, Spin } from 'antd';
import { 
  HddOutlined, 
  DesktopOutlined, 
  DatabaseOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { formatBytes, formatUptime, getStatusColor } from '../../config/performanceDashboard';
import './PerformanceDashboard.css';

const SystemResources = memo(({ 
  resourceData, 
  loading = false, 
  className = '',
  style = {} 
}) => {
  if (loading) {
    return (
      <Card 
        title="System Resources" 
        className={`system-resources ${className}`}
        style={style}
      >
        <div className="system-resources-loading">
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} 
            size="large"
          />
        </div>
      </Card>
    );
  }

  if (!resourceData) {
    return (
      <Card 
        title="System Resources" 
        className={`system-resources ${className}`}
        style={style}
      >
        <div className="system-resources-no-data">
          <InfoCircleOutlined style={{ fontSize: 48, color: '#8c8c8c' }} />
          <p>No resource data available</p>
        </div>
      </Card>
    );
  }

  const { systemMemory, cpu, process, platform } = resourceData;

  // Memory usage component
  const MemoryUsage = ({ title, used, total, percentage, icon }) => {
    const getStatusColor = (percentage) => {
      if (percentage >= 90) return '#ff4d4f';
      if (percentage >= 70) return '#faad14';
      return '#52c41a';
    };

    return (
      <div className="memory-usage-item">
        <div className="memory-usage-header">
          <span className="memory-usage-icon">{icon}</span>
          <span className="memory-usage-title">{title}</span>
        </div>
        <div className="memory-usage-content">
          <Progress
            percent={percentage}
            strokeColor={getStatusColor(percentage)}
            showInfo={false}
            className="memory-usage-progress"
          />
          <div className="memory-usage-details">
            <span className="memory-usage-used">
              {formatBytes(used)}
            </span>
            <span className="memory-usage-separator">/</span>
            <span className="memory-usage-total">
              {formatBytes(total)}
            </span>
            <span className="memory-usage-percentage">
              ({percentage.toFixed(1)}%)
            </span>
          </div>
        </div>
      </div>
    );
  };

  // CPU usage component
  const CpuUsage = ({ usage, loadAverage, count }) => {
    const getStatusColor = (usage) => {
      if (usage >= 90) return '#ff4d4f';
      if (usage >= 70) return '#faad14';
      return '#52c41a';
    };

    return (
      <div className="cpu-usage-item">
        <div className="cpu-usage-header">
          <DesktopOutlined />
          <span>CPU Usage</span>
        </div>
        <div className="cpu-usage-content">
          <Progress
            percent={usage}
            strokeColor={getStatusColor(usage)}
            showInfo={false}
            className="cpu-usage-progress"
          />
          <div className="cpu-usage-details">
            <div className="cpu-usage-main">
              <span className="cpu-usage-value">{usage.toFixed(1)}%</span>
              <span className="cpu-usage-cores">({count} cores)</span>
            </div>
            <div className="cpu-usage-load">
              <span className="cpu-usage-load-label">Load Average:</span>
              <span className="cpu-usage-load-value">
                {loadAverage.map((load, index) => (
                  <span key={index} className="cpu-load-item">
                    {load.toFixed(2)}
                    {index < loadAverage.length - 1 && ', '}
                  </span>
                ))}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Process memory component
  const ProcessMemory = ({ memory, uptime }) => {
    return (
      <div className="process-memory-item">
        <div className="process-memory-header">
          <DatabaseOutlined />
          <span>Process Memory</span>
        </div>
        <div className="process-memory-content">
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <div className="process-memory-detail">
                <span className="process-memory-label">RSS:</span>
                <span className="process-memory-value">
                  {formatBytes(memory?.rss || 0)}
                </span>
              </div>
            </Col>
            <Col span={12}>
              <div className="process-memory-detail">
                <span className="process-memory-label">Heap Total:</span>
                <span className="process-memory-value">
                  {formatBytes(memory?.heapTotal || 0)}
                </span>
              </div>
            </Col>
            <Col span={12}>
              <div className="process-memory-detail">
                <span className="process-memory-label">Heap Used:</span>
                <span className="process-memory-value">
                  {formatBytes(memory?.heapUsed || 0)}
                </span>
              </div>
            </Col>
            <Col span={12}>
              <div className="process-memory-detail">
                <span className="process-memory-label">External:</span>
                <span className="process-memory-value">
                  {formatBytes(memory?.external || 0)}
                </span>
              </div>
            </Col>
          </Row>
          <div className="process-memory-uptime">
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            <span className="process-memory-uptime-label">Process Uptime:</span>
            <span className="process-memory-uptime-value">
              {formatUptime(uptime || 0)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Platform info component
  const PlatformInfo = ({ platform }) => {
    return (
      <div className="platform-info-item">
        <div className="platform-info-header">
          <InfoCircleOutlined />
          <span>Platform Information</span>
        </div>
        <div className="platform-info-content">
          <Row gutter={[16, 8]}>
            <Col span={8}>
              <div className="platform-info-detail">
                <span className="platform-info-label">Type:</span>
                <span className="platform-info-value">
                  {platform?.type || 'N/A'}
                </span>
              </div>
            </Col>
            <Col span={8}>
              <div className="platform-info-detail">
                <span className="platform-info-label">Architecture:</span>
                <span className="platform-info-value">
                  {platform?.arch || 'N/A'}
                </span>
              </div>
            </Col>
            <Col span={8}>
              <div className="platform-info-detail">
                <span className="platform-info-label">Version:</span>
                <span className="platform-info-value">
                  {platform?.version || 'N/A'}
                </span>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  return (
    <Card 
      title="System Resources" 
      className={`system-resources ${className}`}
      style={style}
    >
      <div className="system-resources-content">
        {/* System Memory */}
        {systemMemory && (
          <div className="system-resources-section">
            <h4>Memory Usage</h4>
            <MemoryUsage
              title="System Memory"
              used={systemMemory.used}
              total={systemMemory.total}
              percentage={systemMemory.percentage}
              icon={<HddOutlined />}
            />
          </div>
        )}

        {/* CPU Usage */}
        {cpu && (
          <div className="system-resources-section">
            <h4>CPU Usage</h4>
            <CpuUsage
              usage={cpu.usage}
              loadAverage={cpu.loadAverage}
              count={cpu.count}
            />
          </div>
        )}

        {/* Process Memory */}
        {process && (
          <div className="system-resources-section">
            <h4>Process Memory</h4>
            <ProcessMemory
              memory={process.memory}
              uptime={process.uptime}
            />
          </div>
        )}

        {/* Platform Information */}
        {platform && (
          <div className="system-resources-section">
            <h4>Platform Information</h4>
            <PlatformInfo platform={platform} />
          </div>
        )}
      </div>
    </Card>
  );
});

SystemResources.displayName = 'SystemResources';

export default SystemResources;
